import { Avatar, Box, Chip, Divider, Typography } from '@mui/material';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import dayjs from 'dayjs';
import { formatDealAmount } from 'helper/short-currency';
import { DealsService } from 'service/deals.service';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setOwner, setStage } from 'store/slices/dealsSlice';
import zeplinColor from 'theme/Color';
import DealDropdown from '../../DealDropdown';
import { capitalizeStage, formatPipelineType } from '../../utils';

const InnerInformation = ({ data }: { data: any }) => {
  const { dealValue, clientName, pipelineType, stageName, closeDate, ownerName } = data;
  const formattedCreationDate = closeDate ? dayjs(closeDate).format('MMM D, YYYY') : 'N/A';
  const dispatch = useAppDispatch();

  const map = [
    { type: 'text', label: 'Deal value', value: `$${formatDealAmount(dealValue)}` },
    { type: 'avatar', label: 'Client name', value: clientName },
    { type: 'chip', label: 'Pipeline', value: pipelineType },
    {
      type: 'select',
      label: 'Stage',
      value: capitalizeStage(stageName),
      options: [
        { label: 'Test 1', value: 'test1' },
        { label: 'Test 2', value: 'test2' },
      ],
      onChange: () => {},
    },
    { type: 'text', label: 'Close date', value: formattedCreationDate },
    {
      type: 'select',
      label: 'Opportunity Owner',
      value: ownerName,
      options: [
        { label: 'Test 1', value: 'test1' },
        { label: 'Test 2', value: 'test2' },
      ],
      onChange: () => {},
    },
  ];

  const { owners, stages, stage, owner } = useAppSelector((state) => state.deals);

  const { startOperation, alertState, setAlertState, getAlertContent, completeOperation } =
    useAlertStatus();

  const { dealId } = useAppSelector((state) => state.deals.deal);

  const handleFieldChange = async (fieldName: string, newValue: string) => {
    if (!dealId) return;

    try {
      startOperation('update', 'Updating deal...');
      await DealsService.updateDeal(dealId, { [fieldName]: newValue });

      completeOperation('update', 'Deal updated successfully.');
    } catch (error: any) {
      completeOperation('update', error?.response?.data?.message || 'Failed to update deal', true);
    }
  };

  const handleOwnerChange = (fieldName: string, value: string) => {
    dispatch(setOwner(value));
    handleFieldChange(fieldName, value);
  };
  const handleStageChange = (fieldName: string, value: string) => {
    dispatch(setStage(value));
    handleFieldChange(fieldName, value);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px' }}>
        {map.map(({ label, value, type }) => (
          <>
            <Box
              key={label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: '8px',
              }}
            >
              {type === 'chip' && (
                <Chip
                  label={
                    <Typography sx={{ color: zeplinColor.BasicBlack }} variant='responsiveLabel1'>
                      {formatPipelineType(pipelineType)}
                    </Typography>
                  }
                  size='small'
                  sx={{ bgcolor: zeplinColor.Neutral1 }}
                />
              )}

              {type === 'select' && label === 'Stage' && (
                <DealDropdown
                  options={stages}
                  value={stage}
                  setValue={handleStageChange}
                  fieldName='stageName'
                />
              )}

              {type === 'select' && label === 'Opportunity Owner' && (
                <DealDropdown
                  options={owners}
                  value={owner}
                  setValue={handleOwnerChange}
                  fieldName='ownerName'
                />
              )}

              {type === 'text' && (
                <Typography variant='responsiveBody2' sx={{ color: zeplinColor.Neutral1 }}>
                  {value}
                </Typography>
              )}

              {type === 'avatar' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem' }}>
                    {value?.charAt(0)}
                  </Avatar>
                  <Typography variant='responsiveBody2' sx={{ color: zeplinColor.Neutral1 }}>
                    {value}
                  </Typography>
                </Box>
              )}

              <Typography variant='responsiveBody2' sx={{ color: zeplinColor.Neutral1 }}>
                {label}
              </Typography>
            </Box>
            <Divider
              sx={{
                borderWidth: '1px',
                borderColor: zeplinColor.Neutral6,
                height: '32px',
                alignSelf: 'center',
                ':last-child': {
                  display: 'none',
                },
              }}
            />
          </>
        ))}
      </Box>

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </>
  );
};

export default InnerInformation;
